import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store/store';
import i18n from '@/config/i18n.config';
import config from '@/config/app.config';
import AuthService from '@/services/auth.service';

Vue.use(Router);

// The guards below re-issue navigations (to append ?lang, or to bounce an
// unauthenticated visitor to the login page). Since vue-router 3.1 that makes
// the original push() reject, so every navigation missing ?lang — deleting an
// invoice, for one — surfaced as an unhandled rejection. These outcomes are
// expected, so resolve them instead of throwing; anything else still throws.
// These live as statics on the default export; the ESM build has no named
// exports for them. isNavigationFailure compares types with ===, so each one
// has to be checked on its own rather than as a combined mask.
const EXPECTED_FAILURES = [
  Router.NavigationFailureType.redirected,
  Router.NavigationFailureType.duplicated,
];

function ignoreExpectedFailures(navigate) {
  return function navigateSafely(location, onComplete, onAbort) {
    if (onComplete || onAbort) {
      return navigate.call(this, location, onComplete, onAbort);
    }
    return navigate.call(this, location).catch((err) => {
      if (EXPECTED_FAILURES.some(type => Router.isNavigationFailure(err, type))) {
        return err;
      }
      throw err;
    });
  };
}

Router.prototype.push = ignoreExpectedFailures(Router.prototype.push);
Router.prototype.replace = ignoreExpectedFailures(Router.prototype.replace);

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "login" */ '@/views/Login.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    name: 'dashboard',
    redirect: 'invoices',
    component: () => import(/* webpackChunkName: "dashboard" */ '@/views/dashboard/Dashboard.vue'),
    beforeEnter: async (to, from, next) => {
      await store.dispatch('teams/init');
      next();
    },
    children: [
      {
        path: '/invoices',
        name: 'invoices',
        component: () => import(/* webpackChunkName: "invoices" */ '@/views/dashboard/Invoices.vue'),
      },
      {
        path: '/invoice/:id',
        name: 'invoice',
        component: () => import(/* webpackChunkName: "invoice" */ '@/views/dashboard/Invoice.vue'),
      },
    ],
  },
  {
    path: '/invoices/:id/print',
    name: 'invoice-print',
    component: () => import(/* webpackChunkName: "invoice" */ '@/views/InvoicePrint.vue'),
  },
];

const router = new Router({
  mode: 'history',
  base: config.base_url,
  routes,
});

router.beforeEach((to, from, next) => {
  // Auth guard for non-public routes when using server storage
  if (config.storageType === 'http' && !to.meta.public && !AuthService.isLoggedIn()) {
    return next({ name: 'login' });
  }

  if (!to.query.hasOwnProperty('lang')) {
    i18n.initialized.then(() => {
      to.query.lang = i18n.i18next.language;
      next(to);
    });
  } else {
    next();
  }
});

export default router;
